import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { indexOf, sortBy, uniq, filter, values, reduce, keyBy, isArray, delay } from 'lodash';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import Fa from 'react-fontawesome';
import pluralize from 'pluralize';
import NumberDisplay from '../../../../components/number-display';
import OwnerNameRender from '../../../../components/table-cells/SaleRepsNameRenderer';
import OpportunityNameRenderer from '../../../../components/table-cells/OpportunityNameRenderer';
import CustomMetaFieldRenderer from '../../../../components/table-cells/CustomMetaFieldRenderer';
import MoneyDisplay from '../../../../components/money-display';
import StageDisplay from './Stage';
import UniModal from '../../../../components/uni-modal';
import ContactCell from '../../../../components/contact-cell'

import styles from './styles.css';

const outcomes = {
  1: 'Win', 2: 'Lost', 3: 'No Decision', 4: 'Unknown', 5: 'Pending', 6: 'Experience',
};

const expandedOpps = {};

class OpportunityRow extends Component {
  constructor(props) {
    super(props);
    this.state = {
    };
    this.requestAddSurveyForContact = this.requestAddSurveyForContact.bind(this);
    this.toggleShowAIPeople = this.toggleShowAIPeople.bind(this);
  }

  requestAddSurveyForContact(contact) {
    this.props.requestAddOpportunitySurveyForContact(contact);
  }

  toggleShowAIPeople(e) {
    e.preventDefault();
    e.stopPropagation();
    const { opportunity } = this.props;
    const id = opportunity.opportunity_id;
    if (expandedOpps[id]) {
      expandedOpps[id] = false;
    } else {
      expandedOpps[id] = true;
    }
    this.props.toggleShowAIPeople(id);
  }

  renderStageVerb() {
    const { opportunity } = this.props;
    let { stage } = opportunity;
    if (opportunity.has_feedbacks && opportunity.tv_outcome) {
      const tvOutcome = opportunity.tv_outcome;
      if (outcomes[tvOutcome]) {
        stage = outcomes[tvOutcome];
      }
    }
    return <StageDisplay isPast={false} type="noun" value={stage} />
  }

  renderLabel() {
    const { opportunity, row } = this.props;
    let { label } = row;
    if (row.tag === 'contactDetails') {
      const contacts = opportunity.contact_roles || [];
      label = (
        <NumberDisplay value={contacts.length} suffix="contact" suffixIsUnit precision={0} />
      );
    }
    return label;
  }

  renderOwnerName() {
    const { opportunity: { owner }, isAccount } = this.props;
    if (!owner) {
      return null;
    }
    const name = owner.name || owner.display_name;
    const nameComp = (
      <em>
        <OwnerNameRender popover isOwner value={name} data={owner} />
      </em>
    );
    if (isAccount) {
      return nameComp;
    }
    return (
      <span>
        &nbsp; &mdash; <span className={styles.serif}>by</span> {nameComp}
      </span>
    );
  }

  renderOpportunityName() {
    const { opportunity } = this.props;
    return (
      <div>
        <OpportunityNameRenderer value={opportunity.name} data={opportunity} />
        {this.renderOwnerName()}
      </div>
    );
  }

  renderIndustry() {
    const { opportunity: { account } } = this.props;
    if (!account) {
      return '(unknown)';
    }
    const industries = account.industries || [account.industry];
    if (!industries || industries.length === 0) {
      return '(unknown)';
    }
    return (
      <span>({industries.join(', ')})</span>
    );
  }

  renderOpportunityTypes() {
    const { opportunity: { types, amount } } = this.props;
    return (
      <div>
        <MoneyDisplay value={amount} /> {(types || []).join(', ')} {this.renderStageVerb()} {this.renderIndustry()}
      </div>
    );
  }

  renderOpportunityContacts() {
    const { rowData, row, opportunity, surveys } = this.props;
    const contactRoles = sortBy(values(keyBy(opportunity.contact_roles || [], 'contact_id')), (c, i) => c.is_primary ? -1 : i + 1);
    if (contactRoles.length === 0) {
      return (<em>No contacts associated with the opportunity.</em>);
    }

    const id = opportunity.opportunity_id;
    if (!rowData[id] || !rowData[id][row._id]) {
      return this.renderCustomField(true);
    }

    let vals = rowData[id][row._id] || [];

    if (!isArray(vals)) {
      vals = [vals];
    }

    const contactInfos = keyBy(vals, 'contact_id');
    const surveyInfos = keyBy(surveys, 'contact_id');
    const primaryTip = (<Tooltip id={`${opportunity.opportunity_id}-tip`}>Primary contact</Tooltip>);

    
    let countPeopleAI = 0;
    // expandedOpps
    const showPeopleAi = expandedOpps[id] === true;

    const contacts = reduce(contactRoles, (items, contactRole, i) => {
      const contactInfo = contactInfos[contactRole.contact_id] || {};
      const isPeopleAi = contactRole.role === 'Created by People.ai';
      if (isPeopleAi) {
        countPeopleAI += 1;
      }
      if (!isPeopleAi || showPeopleAi) {
        const surveyInfo = surveyInfos[contactRole.contact_id];
        const hasSurvey = surveyInfo !== null && surveyInfo !== undefined;
        const contact = {
          ...contactRole,
          ...contactInfo,
          hasSurvey,
          survey: surveyInfo,
        };
        items.push(contact);
      }
      return items;
    }, []);

    const total = contacts.length;

    const contactCells = reduce(contacts, (items, contact, i) => {
      const cls = [];
      if (contact.hasSurvey) {
        cls.push('text-success');
      }
      if (!contact.is_primary) {
        cls.push('text-bold');
      }
      const label = contact.is_primary ? (<strong>{contact.name} <OverlayTrigger placement="top" overlay={primaryTip}><Fa name="star" className="text-warning" /></OverlayTrigger></strong>) : contact.name;

      const footer = !contact.hasSurvey ? (
        <a className={cls.join(' ')} href="" onClick={(e) => { e.preventDefault(); this.requestAddSurveyForContact(contact) }}><Fa name="comments-o" /> Get Feedback</a>
      ) : null;

      const main = (<span className={cls.join(' ')}>{label}</span>);

      const modal = (
        <UniModal
        key={`${contact.contact_id}-main`}
          anchor={main}
          footer={footer}
        >
          <ContactCell
            contact={contact}
          />
        </UniModal>
      );
      items.push(modal);
      items.push((<em key={`${contact.contact_id}-role`}> ({contact.role ? contact.role : 'No role'})</em>));
      if (i + 1 < total) {
        items.push(<span key={`${contact.contact_id}-comma`}>, </span>);
      }
      return items;
    }, []);


 

    return (
      <div>
        {contactCells}
        {countPeopleAI > 0 && <div><a href="" onClick={this.toggleShowAIPeople}><Fa name={showPeopleAi ? 'caret-up' : 'caret-down'} /> {showPeopleAi ? 'hide all' : `show ${countPeopleAI}`} People.ai {pluralize('Contact', countPeopleAI)}</a></div>}
      </div>
    );
  }

  renderOpportunityProducts() {
    const { rowData, row, opportunity, productDictionary } = this.props;
    const lineItems = filter(opportunity.line_items, { show: true });

    const noProducts = (<em>No products associated with the opportunity.</em>);
    if (lineItems.length === 0) {
      return noProducts;
    }
    const id = opportunity.opportunity_id;

    if (!rowData[id] || !rowData[id][row._id]) {
      return this.renderCustomField(true);
    }

    let vals = rowData[id][row._id] || [];

    if (!isArray(vals)) {
      vals = [vals];
    }

    const indexed = keyBy(opportunity.line_items, 'product_id');

    const products = reduce(vals, (items, product) => {
      if (indexed[product.product_id]) {
        items.push({
          ...product,
          mapped: productDictionary[product.product_id] !== undefined && productDictionary[product.product_id].length > 0,
        });
      }
      return items;
    }, []);

    if (products.length === 0) {
      return noProducts;
    }

    const total = products.length;
    return (
      <div>
        {products.map((product, i) => {
          const cls = product.mapped ? '' : 'text-danger';
          return [
            (<span className={cls} key={product.product_id}>{product.name}</span>),
            ...(i + 1 < total ? [(<span key={`${product.product_id}-comma`}>, </span>)] : []),
          ];
        })}
      </div>
    );
  }

  getRowType() {
    const { row: { type } } = this.props;

    let columnType = 'string';
    if (indexOf(['int', 'double', 'number', 'currency', 'percent'], type) >= 0) {
      columnType = 'numericColumn';
    } else if (indexOf(['date'], type) >= 0) {
      columnType = 'dateColumn';
    }
    return columnType;
  }

  getIsLoading() {
    const { rowData, row, opportunity, isAccount } = this.props;
    const data = opportunity;
    const idAttr = isAccount ? 'account_id' : 'opportunity_id';

    const id = data ? data[idAttr] : null;

    if (!id || !rowData[id]) {
      return null;
    }
    return rowData[id].$loading === true;
  }

  rowCustomValueGetter() {
    const { rowData, row, opportunity, isAccount } = this.props;
    const isCustomMeta = false;
    const data = opportunity;
    const idAttr = isAccount ? 'account_id' : 'opportunity_id';

    const id = data ? data[idAttr] : null;

    if (!id || !rowData[id]) {
      return null;
    }
    const vals = rowData[id][row._id];

    let objValues = null;
    if (isArray(vals)) {
      objValues = vals;
    } else if (vals !== undefined && vals !== null) {
      objValues = [vals];
    }

    if (isCustomMeta && objValues?.length === 0) {
      // return default_value
      return row.default_value;
    }
    if (objValues?.length === 1) {
      if (row.type === 'date') {
        const m = objValues[0] ? moment(objValues[0]) : null;
        return m && m.isValid() ? m.toDate() : null;
      }
      return objValues[0];
    }
    return uniq(objValues);
  }

  renderCustomField(hidden = false) {
    const { row, opportunity } = this.props;
    const isLoading = this.getIsLoading();

    const columnType = this.getRowType();
    if (isLoading) {
      return (<Fa name="spinner" spin className="text-muted" />);
    }
    return (
      <CustomMetaFieldRenderer
        column={row}
        data={opportunity}
        hidden={hidden}
        customFieldType={columnType}
        value={this.rowCustomValueGetter()}
      />
    );
  }

  renderContent() {
    const { row } = this.props;
    let content = '';
    if (row.tag === 'opportunity') {
      content = this.renderOpportunityName();
    } if (row.tag === 'owner') {
      content = this.renderOwnerName();
    } else if (row.tag === 'opportunityType') {
      content = this.renderOpportunityTypes();
    } if (row.tag === 'contactDetails') {
      content = this.renderOpportunityContacts();
    } if (row.tag === 'productDetails') {
      content = this.renderOpportunityProducts();
    } if (!row.is_system) {
      content = this.renderCustomField();
    }
    return content;
  }

  render() {
    const { row, opportunity, isAccount } = this.props;
    let hasDescription = row.description && row.description.length > 0;
    // const idAttr = isAccount ? 'account_id' : 'opportunity_id';
    let tooltip = hasDescription ? (
      <Tooltip id={`${opportunity[isAccount]}-${row._id}_tooltip`}>{row.description}</Tooltip>
    ) : null;
    if (!tooltip && row.tag === 'productDetails') {
      hasDescription = true;
      tooltip = (<Tooltip id={`${opportunity[isAccount]}-${row._id}_tooltip`}>If a product name is displayed in &ldquo;red&rdquo;, this indicates that product has not been enabled in your survey.</Tooltip>);
    }

    return (
      <tr key={row._id}>
        <th width="100">
          <nobr>
            {this.renderLabel()}
            {hasDescription && ' '}
            {hasDescription &&
              <OverlayTrigger placement="right" overlay={tooltip}>
                <Fa name="info-circle" className="text-active" />
              </OverlayTrigger>
            }
            {': '}
          </nobr>
        </th>
        <td>
          {this.renderContent()}
        </td>
      </tr>
    );
  }
}

OpportunityRow.propTypes = {
  row: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    description: PropTypes.string,
    tag: PropTypes.string,
    label: PropTypes.string,
    is_system: PropTypes.bool,
    type: PropTypes.string,
    target_object: PropTypes.string,
    target_custom_meta_id: PropTypes.string,
    target_field: PropTypes.string,
    default_value: PropTypes.string,
  }).isRequired,
  rowData: PropTypes.shape({}),
  opportunity: PropTypes.shape({
    name: PropTypes.string,
    amount: PropTypes.number,
    opportunity_id: PropTypes.string,
    stage: PropTypes.string,
    close_date: PropTypes.oneOfType([
      PropTypes.instanceOf(Date),
      PropTypes.instanceOf(moment),
    ]),
    line_items: PropTypes.arrayOf(PropTypes.shape({})),
    owner: PropTypes.shape({
      name: PropTypes.string,
      display_name: PropTypes.string,
    }),
    account:  PropTypes.shape({
      industries: PropTypes.arrayOf(PropTypes.string),
      industry: PropTypes.string,
    }),
    has_feedbacks: PropTypes.bool,
    tv_outcome: PropTypes.number,
    contact_roles: PropTypes.arrayOf(PropTypes.shape({})),
    types: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  productDictionary: PropTypes.shape({}),
  surveys: PropTypes.arrayOf(PropTypes.shape({})),
  requestAddOpportunitySurveyForContact: PropTypes.func,
  isAccount: PropTypes.bool,
  toggleShowAIPeople: PropTypes.func,
};

OpportunityRow.defaultProps = {
  rowData: {},
  productDictionary: {},
  surveys: [],
  isAccount: false,
  toggleShowAIPeople: () => {},
  requestAddOpportunitySurveyForContact: () => {},
};

export default OpportunityRow;
